import { handleActions } from 'redux-actions';
import { getFormValues, change } from 'redux-form';
import { LOCATION_CHANGE, replace } from 'react-router-redux';

import cloneDeep from 'lodash/cloneDeep';
import orderBy from 'lodash/orderBy';
import values from  'lodash/values';
import find from 'lodash/find';

import moment from 'moment';

import http from '../shared/http';
import intl from '../shared/intl';

import checkPermissions from '../shared/check-permissions';

// actions types
const FETCH_DATA_SUCCESS = 'contests/fetch-data-success';
const FETCH_DATA_ERROR = 'contests/fetch-data-error';

const SUBMIT_SUCCESS = 'contests/submit-success';
const SUBMIT_ERROR = 'contests/submit-error';

const REMOVE_SUCCESS = 'contests/remove-success';
const REMOVE_ERROR = 'contests/remove-error';

const SORT_BY_COLUMN = 'contests/table-sort-by-column';

const TOGGLE_PROGRESS_BAR = 'contests/toggle-progress-bar';

// permissions
export const allowedPermissions = {
	'contestView': 'contest_view',
	'contestEdit': 'contest_edit'
};

// initial state
const initialState = {
	data: null,
	showProgressBar: false,
	table: {
		columns: {
			id: {
				index: 'id',
				title: 'ID',
				direction: 'asc'
			},
			name: {
				index: 'name',
				title: 'Name',
				direction: 'asc'
			},
			starts_at: {
				index: 'starts_at',
				title: 'Starts at',
				direction: 'desc'
			},
			ends_at: {
				index: 'ends_at',
				title: 'Ends at',
				direction: 'desc'
			},
			config: {
				index: 'config',
				title: 'Config'
			},
			actions: {
				title: 'Actions'
			}
		},
		sortByColumn: {
			index: 'starts_at',
			title: 'Starts at',
			direction: 'desc'
		},
		rowsNumber: 1000
	},
	form: {},
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: (props) => (dispatch, getState) => {
			(http({
				url: 'v2/contests',
				method: 'get',
				actions: [
					FETCH_DATA_SUCCESS,
					FETCH_DATA_ERROR
				]
			}))(dispatch);
		},
		updateConfig: () => (dispatch, getState) => {
			const contestRulesForm = getFormValues('contestRulesForm')(getState());
			const fields = selectors.getDashboardConfigFields(getState()).map(filed => filed.id);
			const oldConfig = JSON.parse(contestRulesForm.config || '{}');

			for (const field of fields) {
				oldConfig[field] = contestRulesForm[field];
				if (!oldConfig[field]) {
					delete oldConfig[field];
				}
			}
			dispatch(change('contestRulesForm', 'config', JSON.stringify(oldConfig)));
		},

		submitForm: (formData) => (dispatch, getState) => {
			const request = http({
				url: 'v2/contests',
				method: 'post',
				actions: [
					SUBMIT_SUCCESS,
					SUBMIT_ERROR
				],
				data: {
					...formData,
					starts_at: moment(formData.starts_at).format('YYYY-MM-DD HH:mm:ss'),
					ends_at: moment(formData.ends_at).format('YYYY-MM-DD HH:mm:ss'),
					config: JSON.parse(formData.config)
				}
			});

			return request(dispatch)
				.then(action => {
					if (action.type === SUBMIT_SUCCESS) {
						dispatch(actions.redirectToModuleIndex());
					}
				});
		},

		remove: id => dispatch => {
			const request = http({
				url: `v2/contests/${id}`,
				method: 'delete',
				actions: [
					REMOVE_SUCCESS,
					REMOVE_ERROR
				]
			});

			dispatch(actions.toggleProgressBar());

			request(dispatch);
		},

		redirectToModuleIndex: () => dispatch => {
			setTimeout(() => dispatch(replace('contests/')), 100);
		},

		sortByColumn: columnIndex => ({
			type: SORT_BY_COLUMN,
			payload: columnIndex
		}),

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		})
	}
);

// reducers
const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			return initialState;
		},
		[FETCH_DATA_SUCCESS]: (state, action) => {
			return { ...state, data: action.payload.data, showProgressBar: false, error: false };
		},
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[SORT_BY_COLUMN]: (state, action) => {
			const { sortByColumn, columns }  = state.table;
			const columnIndex = action.payload;
			const direction = sortByColumn['index'] == columnIndex ?
				sortByColumn.direction === 'desc' ? 'asc' : 'desc' :
				columns[columnIndex].direction;

			return {
				...state,
				table: {
					...state.table,
					sortByColumn: {
						...columns[columnIndex],
						direction
					}
				}
			};
		},
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[REMOVE_SUCCESS]: state => ({...state, data: null}),
		[REMOVE_ERROR]: state => ({...state, error: true}),
		[SUBMIT_ERROR]: (state, action) => {
			const { response: {status = null, data = null} } = action.payload;
			if (status === 400 && !!data) return state;
			return { ...state, error: true };
		}
	}
);
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.contests.data,
	getTable: state => {
		const { table, data } = state.contests;
		const rows = state.contests.data ? sortColumns(data, table.sortByColumn) : [];
		return {
			headers: values(table.columns),
			rows: rows,
			sortedColumn: table.sortByColumn,
			rowsNumber: table.rowsNumber,
			totalRows: rows.length
		};
	},
	getIntl: state => intl(state),
	getProgressBar: state => state.contests.showProgressBar,
	getDashboardConfigFields: state => state.root.lists['dashboard-config-fields'].map(field => field.id),
	getFieldsById: state => ruleId => {
		const current = find(state.contests.data, { id: parseInt(ruleId) });
		const reservedFields = selectors.getDashboardConfigFields(state)
			.reduce((acc, val) => {
				if (current && current.config) {
					acc[val.id] = current.config[val.id] || '';
				}
				return acc;
			}, {});

		return current ? {
			...state.contests.form,
			...current,
			...reservedFields,
			config: JSON.stringify(current.config)
		} : state.contests.form;
	},
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.contests.error
};

// functions
const sortColumns = (rows, sortedColumn) => {
	if (!rows) return null;
	return orderBy(rows, row => row[sortedColumn.index], sortedColumn.direction);
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/contests/index.js